import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const MOUNTAINS =
  "https://static.prod-images.emergentagent.com/jobs/3d06aecc-7c40-4cf7-8c1b-6c21e0f317e1/images/e5acdeccc459202e0a8cf268f0e64670c55fa6a0ff813686db4f43159ec0c3fa.png";

const chapters = [
  {
    num: "01",
    kicker: "Chapter One",
    title: "The Origin",
    body: "Every HueBleu garment begins on a small farm in regional New South Wales, where long-staple Australian cotton is hand-graded by people whose families have worked the same fields for four generations.",
  },
  {
    num: "02",
    kicker: "Chapter Two",
    title: "The Workshop",
    body: "The cloth travels to our atelier in Katoomba, where it is brushed, garment-dyed in small batches, and finished by a team of nine. We release no more than 220 pieces per drop.",
  },
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);

  // Reveal words one by one
  const headline =
    "Born in the silence between two mountains, where the air still smells of eucalyptus and the cotton breathes like the morning fog.";
  const words = headline.split(" ");

  return (
    <section
      ref={ref}
      id="about"
      data-testid="about-section"
      className="relative py-40 md:py-56 overflow-hidden"
    >
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0 opacity-25"
      >
        <img
          src={MOUNTAINS}
          alt="Blue Mountains"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1d] via-[#0a0f1d]/70 to-[#0a0f1d] z-10" />

      <div className="relative z-20 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-12 gap-6 mb-20"
        >
          <span className="label-overline col-span-12 md:col-span-3">
            01 · The Manifesto
          </span>
          <span className="col-span-12 md:col-span-9 text-white/60 text-sm tracking-[0.2em] uppercase">
            A house of slow craft
          </span>
        </motion.div>

        <h2
          data-testid="about-headline"
          className="font-serif-display text-white text-[36px] md:text-[56px] lg:text-[78px] leading-[1.05] font-light max-w-[1100px]"
        >
          {words.map((w, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{
                duration: 0.9,
                delay: i * 0.04,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="inline-block mr-[0.25em]"
            >
              {w === "fog." || w === "fog" ? (
                <em className="text-[#D4AF37] italic font-light">{w}</em>
              ) : (
                w
              )}
            </motion.span>
          ))}
        </h2>

        {/* Manifesto — numbered chapters */}
        <div className="grid grid-cols-12 gap-x-8 gap-y-16 mt-32">
          {chapters.map((c, i) => (
            <motion.div
              key={i}
              data-testid={`manifesto-chapter-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
              className={`col-span-12 md:col-span-5 ${
                i === 1 ? "md:col-start-8 md:translate-y-16" : ""
              }`}
            >
              <div className="border-t border-[#D4AF37]/25 pt-8">
                <div className="flex items-start gap-6">
                  <span className="ghost-numeral text-7xl md:text-8xl leading-[0.8] italic select-none">
                    {c.num}
                  </span>
                  <div className="pt-2">
                    <span className="label-overline">{c.kicker}</span>
                    <h4 className="mt-3 font-serif-display text-3xl md:text-4xl font-light text-white">
                      {c.title}
                    </h4>
                  </div>
                </div>
                <p className="mt-8 text-white/75 leading-[1.9] text-[15px]">
                  {c.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stat row */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mt-32 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10"
        >
          {[
            ["480", "Grams of heavy cotton"],
            ["9", "Hands per garment"],
            ["220", "Pieces per drop"],
            ["1,043m", "Above sea level"],
          ].map(([n, l], i) => (
            <div
              key={i}
              data-testid={`about-stat-${i}`}
              className="bg-[#0a0f1d] px-6 py-10 md:py-14"
            >
              <div className="font-serif-display text-[#D4AF37] text-5xl md:text-6xl font-light">
                {n}
              </div>
              <div className="mt-3 text-[11px] tracking-[0.25em] uppercase text-white/60">
                {l}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
